import { SortOption, UIState, ViewMode } from '@/types/ui';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

interface UIStore extends UIState {
  setCurrentView: (view: 'artifacts' | 'editor' | 'preview') => void;
  setViewMode: (viewMode: ViewMode) => void;
  setSortOption: (sortOption: SortOption) => void;
  setSearchQuery: (query: string) => void;
  setSelectedArtifactId: (id: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;

  setEditorSplitWidth: (width: number) => void;
  setShowEditor: (show: boolean) => void;
  toggleEditor: () => void;
}

const defaultState: UIState = {
  currentView: 'artifacts',
  viewMode: { type: 'grid', itemsPerRow: 3 },
  sortOption: { field: 'updatedAt', direction: 'desc' },
  searchQuery: '',
  selectedArtifactId: null,
  isLoading: false,
  error: null,
  editorSplitWidth: 50,
  showEditor: true,
};

export const useUIStore = create<UIStore>()(
  devtools(
    persist(
      (set) => ({
        ...defaultState,

        setCurrentView: (view: 'artifacts' | 'editor' | 'preview') => {
          set({ currentView: view });
        },

        setViewMode: (viewMode: ViewMode) => {
          set({ viewMode });
        },

        setSortOption: (sortOption: SortOption) => {
          set({ sortOption });
        },

        setSearchQuery: (query: string) => {
          set({ searchQuery: query });
        },

        setSelectedArtifactId: (id: string | null) => {
          set({ selectedArtifactId: id });
        },

        setLoading: (loading: boolean) => {
          set({ isLoading: loading });
        },

        setError: (error: string | null) => {
          set({ error });
        },

        clearError: () => {
          set({ error: null });
        },

        setEditorSplitWidth: (width: number) => {
          set({ editorSplitWidth: Math.min(Math.max(width, 20), 80) });
        },

        setShowEditor: (show: boolean) => {
          set({ showEditor: show });
        },

        toggleEditor: () => {
          set(state => ({ showEditor: !state.showEditor }));
        },

      }),
      {
        name: 'ui-store',
        partialize: (state) => ({
          viewMode: state.viewMode,
          sortOption: state.sortOption,
          editorSplitWidth: state.editorSplitWidth,
          showEditor: state.showEditor,
        }),
      }
    ),
    {
      name: 'ui-store',
    }
  )
); 